/** The three things an unauthenticated visitor can be told.
 *
 *  Kept deliberately plain. This screen is the one thing a stranger can reach,
 *  so it carries no portfolio, no roster, and no hint of what is inside — just
 *  who to ask.
 */
import type { ReactNode } from "react";

import type { AccessDenied } from "../api";
import { signInHref, useSignOut, type Session } from "../auth";

const VP = "the VP of Business Development";

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="gate">
      <div className="gatecard">
        <div className="brand">
          <span>SPD</span> Services
        </div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

/** Google's own error, surfaced on the way back — an account outside the
 *  domain, or a consent that was declined. */
function authError(): string | null {
  const params = new URLSearchParams(location.search);
  const err = params.get("auth_error");
  if (!err) return null;
  const who = params.get("email");
  if (err === "wrong_domain") {
    // Which domains are allowed is not spelled out here. This screen is
    // pre-authentication, so it is served to anyone who asks, and the list is
    // served from /api/meta, which is not. Naming them would also date badly:
    // spd-ukraine.com is accepted today and will not be forever.
    return `${who ?? "That account"} is not an SPD address. Sign in with your SPD account.`;
  }
  if (err === "unverified_email") return "That Google account has no verified email address.";
  if (err === "access_denied") return "Sign-in was cancelled.";
  return `Sign-in failed (${err}). Try again.`;
}

export function SignIn() {
  const problem = authError();
  return (
    <Shell title="Sign in">
      {problem && <p className="gateerr">{problem}</p>}
      <p>Use your SPD Google account.</p>
      <a className="btn" href={signInHref()}>
        Continue with Google
      </a>
    </Shell>
  );
}

export function NoAccess({ denial }: { denial: AccessDenied }) {
  const signOut = useSignOut();
  return (
    <Shell title="No access">
      <p className="gatecontact">Contact {VP} if you need access.</p>
      <p className="gatewho">
        Signed in as <b>{denial.email}</b>
      </p>
      <button className="btn" onClick={signOut}>
        Sign out
      </button>
    </Shell>
  );
}

/** Renders whichever of the three applies. Never rendered when the session is
 *  in — the app itself takes over then. */
export function Gate({ session }: { session: Session }) {
  if (session.status === "out") return <SignIn />;
  if (session.status === "denied") return <NoAccess denial={session.denial} />;
  if (session.status === "error") {
    return (
      <Shell title="Something went wrong">
        <p className="gateerr">{session.message}</p>
        <a className="btn" href="/">
          Reload
        </a>
      </Shell>
    );
  }
  return <p className="msg">Loading…</p>;
}
